import React, { FC, PropsWithChildren } from 'react';
import { useRouteMatch } from 'react-router-dom';

import {
  WorkspaceContent,
  TeamSettingsNavigation,
  SettingsNavigation,
} from '../../sidebar';

import { cn, useWindowWidth } from '@distribute/frontend/utils';

import { Header, HeaderMobileSidebar } from '../../header';
import { useMenu } from '../hooks';

import { InviteMembersModal } from '../../../features/team-members';
import { ContactSupportFormModal } from '../../../features/contact-support-form';
import { WorkspaceAlertBar } from '../../alert-bar';
import { useDispatch, useSelector } from 'react-redux';
import {
  MobileWalkthroughModal,
  workspaceModel,
} from '../../../features/workspace';
import { useSetWorkspaceAlertBarFromSubscription } from '../../../features/subscription/hooks';
import {
  StripeElementsProvider,
  UpdatePaymentMethodModal,
  ThanksConfirmPaymentMethodModal,
} from '../../../features/subscription';

import {
  SETTINGS_ROUTE,
  CALL_RECORDINGS_ROUTE,
  TEAM_SETTINGS_ROUTE,
  WORKSPACE_ROUTE,
} from '../../../entities/history';

import { PageCreationFlow } from '../../../processes/page-creation-flow';

type Props = {
  className?: string;
  children: React.ReactNode;
};

export const AppLayout: FC<Props> = ({ children }) => {
  const isUserSettings = useRouteMatch(SETTINGS_ROUTE);
  const isTeamSettings = useRouteMatch(TEAM_SETTINGS_ROUTE);
  const isCallRecordings = useRouteMatch(CALL_RECORDINGS_ROUTE);
  const isAllPages = useRouteMatch({ path: WORKSPACE_ROUTE, exact: true });

  const { isMobile, isTablet } = useWindowWidth();

  const { isMenuOpen, onToggleMenu } = useMenu();

  const isShowContactSupportModal = useSelector(
    workspaceModel.selectors.selectIsShowContactSupportModal
  );

  const dispatch = useDispatch();

  const handleContactSupport = () => {
    dispatch(workspaceModel.actions.setIsShowContactSupportModal(true));
  };

  useSetWorkspaceAlertBarFromSubscription();

  const isWorkspace = !isUserSettings && !isTeamSettings;

  return (
    <div className="flex flex-col h-screen relative">
      <WorkspaceAlertBar />
      {isWorkspace && !isMenuOpen && (
        <Header
          onToggleMenu={onToggleMenu}
          onContactSupport={handleContactSupport}
        />
      )}
      <div className="min-h-0 flex flex-grow gap-x-2 md:flex-col bg-gray-100">
        <aside
          className={cn(
            'relative bg-gray-100 z-40 w-72 h-full overflow-y-auto flex flex-col flex-shrink-0 gap-y-4 pl-6 pr-4 md:pl-4 md:w-full md:fixed md:inset-0 md:z-50',
            { 'md:hidden': !isMenuOpen }
          )}
        >
          <HeaderMobileSidebar onToggleMenu={onToggleMenu} />
          {isWorkspace && <WorkspaceContent />}
          {isUserSettings && <SettingsNavigation />}
          {isTeamSettings && <TeamSettingsNavigation />}
        </aside>
        {isCallRecordings || isAllPages ? (
          children
        ) : (
          <AppMainLayout isWorkspace={isWorkspace}>{children}</AppMainLayout>
        )}
        <StripeElementsProvider>
          <UpdatePaymentMethodModal />
        </StripeElementsProvider>
        <ThanksConfirmPaymentMethodModal />
        <InviteMembersModal />
        <ContactSupportFormModal
          isOpen={isShowContactSupportModal}
          onClose={() =>
            dispatch(workspaceModel.actions.setIsShowContactSupportModal(false))
          }
        />
        <PageCreationFlow />
        {(isMobile || isTablet) && <MobileWalkthroughModal />}
      </div>
    </div>
  );
};

type AppMainLayoutProps = {
  isWorkspace: boolean;
};

export const AppMainLayout: FC<PropsWithChildren<AppMainLayoutProps>> = ({
  children,
  isWorkspace,
}) => {
  return (
    <main
      className={cn(
        'relative flex flex-col flex-1 min-w-0 bg-base-white border border-gray-200 md:border-none md:rounded-none rounded-xl',
        {
          'md:m-0 mb-2 mr-2': isWorkspace,
          'md:m-0 my-2 mr-2': !isWorkspace,
        }
      )}
    >
      <div
        className={cn('flex flex-1 overflow-y-auto', {
          'sm:p-5 p-6': isWorkspace,
          'sm:p-4 md:p-6 p-8': !isWorkspace,
        })}
      >
        {children}
      </div>
    </main>
  );
};
